import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleLeft, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { SERVER_BASE } from "../config/server";
import apiClient from "../utils/apiClient";
import moment from "moment";

const MRInfo = ({ accessOfDepartments }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location?.state?.editData;

  const [editId, setEditId] = useState(null);

  const token = localStorage.getItem("token");
  const code = localStorage.getItem("code");
  const email = localStorage.getItem("email");

  const [mrNo, setMrNo] = useState("");
  const [mrDate, setMrDate] = useState("");

  /* ================= LEDGER ================= */
  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [selectedLedger, setSelectedLedger] = useState(null);

  /* ================= PROJECT ================= */
  const [project, setProject] = useState(null);
  const [projectOptions, setProjectOptions] = useState([]);

  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectCode, setNewProjectCode] = useState("");

  const isNewProject = project?.value === "__NEW__";

  /* ================= OTHER ================= */
  const [remarks, setRemarks] = useState("");
  const [itemOptions, setItemOptions] = useState([]);

  const [items, setItems] = useState([
    {
      item: null,
      itemId: null,
      qty: "",
      uom: "",
      remarks: "",
      mmsCode: "",
      mmsName: "",
      mmsQuantity: "",
      secondaryUom: "",
    },
  ]);

  /* ================= MMS OPTIONS ================= */
  const mmsOptions = itemOptions.map((i) => ({
    ...i,
    value: i.itemId,
    label: i.mmsCode || "—",
  }));

  /* =====================================================
     EDIT HANDLER
  ===================================================== */
  const handleEdit = (data) => {
    setEditId(data.id);
    setMrNo(data.mrNo);
    setMrDate(moment(data?.mrDate).format("YYYY-MM-DD"));
    setRemarks(data?.remarks);

    if (data?.ledgerId) {
      setSelectedLedger({ value: data.ledgerId, label: data.ledgerName });
      fetchProjectsByLedger(data.ledgerId);
    }

    setProject({
      value: data?.projectId,
      label: data?.projectName,
    });

    setItems(
      (data.items || []).map((i) => {
        const factor =
          i.primaryQuantity && i.secondaryQuantity && i.primaryQuantity > 0
            ? i.secondaryQuantity / i.primaryQuantity
            : null;

        return {
          mrItemId: i.id,
          itemId: i.itemId,
          item: {
            value: i.itemName,
            label: i.itemName,
            itemId: i.itemId,
            uom: i.uom,
            mmsCode: i.mmsCode,
            mmsName: i.mmsName,
            secondaryUom: i.secondaryUom,
            primaryQuantity: i.primaryQuantity,
            secondaryQuantity: i.secondaryQuantity,
          },
          qty: i.qty,
          uom: i.uom,
          remarks: i.remarks || "",
          mmsCode: i.mmsCode || "",
          mmsName: i.mmsName || "",
          secondaryUom: i.secondaryUom || "",
          mmsQuantity:
            factor && i.qty ? (i.qty * factor) : "",
        };
      })
    );
  };

  useEffect(() => {
    if (editData) handleEdit(editData);
  }, [editData]);

  /* =====================================================
     FETCH ITEMS
  ===================================================== */
  /* =====================================================
  FETCH ITEMS
===================================================== */
  const fetchItems = async () => {
    try {
      const res = await apiClient.get(
        `${SERVER_BASE}/getItemOptionsByDepartment`,
        {
          params: { code, accessOfDepartments },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setItemOptions(
        (res.data || [])
          // ✅ keep only items having BOTH MMS fields
          .filter(
            itm =>
              itm.mmsCode &&
              itm.mmsName &&
              itm.mmsCode.toString().trim() !== "" &&
              itm.mmsName.toString().trim() !== ""
          )
          .map(itm => ({
            value: itm.printName,
            label: `${itm.printName}${itm.additionalNames ? ` - [ ${itm.additionalNames} ]` : ""
              }`,
            itemId: itm.id,
            uom: itm.uom,
            mmsCode: itm.mmsCode,
            mmsName: itm.mmsName,
            secondaryUom: itm.secondaryUom,
            primaryQuantity: itm.primaryQuantity,
            secondaryQuantity: itm.secondaryQuantity,
          }))
      );
    } catch (error) {
      console.error("Failed to fetch items:", error);
      setItemOptions([]); // safe fallback
    }
  };



  /* =====================================================
    FETCH LEDGERS
  ===================================================== */
  const fetchLedgerMasterDetails = async () => {
    try {
      const res = await apiClient.get(`${SERVER_BASE}/ledgerMasters`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setLedgerOptions([
        { value: "Own Center", label: "Own Center" },
        ...(res.data || []).map((l) => ({
          value: l.id,
          label: l.printName,
        })),
      ]);
    } catch (error) {
      console.error("Failed to fetch ledger masters:", error);
      setLedgerOptions([{ value: "Own Center", label: "Own Center" }]);
    }
  };


  /* =====================================================
    FETCH PROJECTS BY LEDGER
  ===================================================== */
  const fetchProjectsByLedger = async (ledgerId) => {
    try {
      const res = await apiClient.get(
        `${SERVER_BASE}/area-projects/${ledgerId}/${code}`
      );

      setProjectOptions([
        { value: "__NEW__", label: "➕ New Project" },
        ...(res.data || []).map((p) => ({
          value: p.id,
          label: `${p.projectName} - [ Code : ${p.projectCode} ]`,
        })),
      ]);
    } catch (error) {
      console.error("Failed to fetch projects by ledger:", error);
      setProjectOptions([
        { value: "__NEW__", label: "➕ New Project" },
      ]);
    }
  };


  useEffect(() => {
    fetchItems();
    fetchLedgerMasterDetails();
  }, [code, accessOfDepartments]);

  /* =====================================================
     HANDLERS
  ===================================================== */
  const handleLedgerChange = (ledger) => {
    setSelectedLedger(ledger);
    setProject(null);
    setProjectOptions([]);
    setNewProjectName("");
    setNewProjectCode("");
    if (ledger?.value) fetchProjectsByLedger(ledger.value);
  };

  const syncItemSelection = (selected, index) => {
    const newItems = [...items];

    if (!selected) {
      newItems[index] = {
        item: null,
        itemId: null,
        qty: "",
        uom: "",
        remarks: "",
        mmsCode: "",
        mmsName: "",
        mmsQuantity: "",
        secondaryUom: "",
      };
      setItems(newItems);
      return;
    }

    const resolvedItem = itemOptions.find(
      (i) => i.itemId === selected.itemId
    );
    if (!resolvedItem) return;

    const factor =
      resolvedItem.primaryQuantity && resolvedItem.secondaryQuantity
        ? resolvedItem.secondaryQuantity / resolvedItem.primaryQuantity
        : null;

    newItems[index].item = resolvedItem;
    newItems[index].itemId = resolvedItem.itemId;
    newItems[index].uom = resolvedItem.uom || "";
    newItems[index].mmsCode = resolvedItem.mmsCode || "";
    newItems[index].mmsName = resolvedItem.mmsName || "";
    newItems[index].secondaryUom = resolvedItem.secondaryUom || "";
    newItems[index].mmsQuantity =
      factor && newItems[index].qty
        ? (newItems[index].qty * factor)
        : "";

    setItems(newItems);
  };

  const handleItemFieldChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    if (field === "qty") {
      const item = newItems[index].item;
      if (
        item &&
        item.primaryQuantity &&
        item.secondaryQuantity &&
        item.primaryQuantity > 0
      ) {
        const factor = item.secondaryQuantity / item.primaryQuantity;
        newItems[index].mmsQuantity = Number(value || 0) * factor;
      } else {
        newItems[index].mmsQuantity = "";
      }
    }

    setItems(newItems);
  };

  const addRow = (index) => {
    const newItems = [...items];
    newItems.splice(index + 1, 0, {
      item: null,
      itemId: null,
      qty: "",
      uom: "",
      remarks: "",
      mmsCode: "",
      mmsName: "",
      mmsQuantity: "",
      secondaryUom: "",
    });
    setItems(newItems);
  };

  const deleteRow = (index) => {
    if (items.length > 1) {
      const newItems = [...items];
      newItems.splice(index, 1);
      setItems(newItems);
    }
  };

  /* =====================================================
     SUBMIT
  ===================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    /* =======================
       COMMON TOAST CONFIG
    ======================= */
    const toast = {
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
    };

    /* =======================
       BASIC VALIDATIONS
    ======================= */
    if (!mrNo) {
      Swal.fire({ ...toast, icon: "warning", title: "MR No is required" });
      return;
    }

    if (!mrDate) {
      Swal.fire({ ...toast, icon: "warning", title: "MR Date is required" });
      return;
    }

    if (!selectedLedger) {
      Swal.fire({ ...toast, icon: "warning", title: "Ledger is required" });
      return;
    }

    /* =======================
       PROJECT VALIDATION
    ======================= */
    if (isNewProject && !newProjectName) {
      Swal.fire({ ...toast, icon: "warning", title: "Project Name is required" });
      return;
    }

    if (isNewProject && !newProjectCode) {
      Swal.fire({ ...toast, icon: "warning", title: "Project Code is required" });
      return;
    }

    /* =======================
       ITEMS VALIDATION
    ======================= */
    // consider an item selected only if it has itemId (or id) AND qty > 0
    const validItems = (items || []).filter(
      (item) =>
        (item.itemId || item.id) && item.qty && Number(item.qty) > 0
    );

    if (validItems.length === 0) {
      Swal.fire({
        ...toast,
        icon: "warning",
        title: "Select at least one item with quantity",
      });
      return;
    }


    /* =======================
       PAYLOAD
    ======================= */
    const payload = {
      mrNo,
      mrDate,
      ledgerId: selectedLedger.value,
      ledgerName: selectedLedger.label,
      projectId: isNewProject ? null : project?.value,
      projectName: isNewProject ? newProjectName : project?.label,
      projectCode: isNewProject ? newProjectCode : null,
      isNewProject,
      remarks,
      createdBy: email,
      items,
      centerCode:code
    };

    /* =======================
       API CALL
    ======================= */
    try {
      if (editId) {
        await apiClient.put(
          `${SERVER_BASE}/update-mr-info/${editId}`,
          payload
        );
      } else {
        await apiClient.post(`${SERVER_BASE}/save-mr-info`, payload);
      }

      Swal.fire({
        ...toast,
        icon: "success",
        title: "MR saved successfully",
      });

      navigate("../MRList");
    } catch (err) {
    }
  };


  return (
    <div className="formcontainer mt-5">
      <div className="d-flex align-items-end mb-3">
        <button
          className="btn btn-secondary btn-sm me-3"
          onClick={() => navigate('../MRList')}
        >
          <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
        </button>
        <h5 className="mb-0">Material Requisition</h5>
      </div>

      {/* HEADER */}
      <div className="row mb-2">
        <div className="col-md-6">
          <label>MR No</label>
          <input className="form-control" value={mrNo} onChange={(e) => setMrNo(e.target.value)} />
        </div>
        <div className="col-md-6">
          <label>MR Date</label>
          <input type="date" className="form-control" value={mrDate} onChange={(e) => setMrDate(e.target.value)} />
        </div>
      </div>

      {/* LEDGER + PROJECT */}
      <div className="row mb-2">
        <div className="col-md-6">
          <label>Ledger</label>
          <Select options={ledgerOptions} value={selectedLedger} onChange={handleLedgerChange} />
        </div>
        <div className="col-md-6">
          <label>Project</label>
          <Select options={projectOptions} value={project} onChange={setProject} isDisabled={!selectedLedger} />
        </div>
      </div>

      {isNewProject && (
        <div className="row mb-2">
          <div className="col-md-6">
            <label>Project Name</label>
            <input className="form-control" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label>Project Code</label>
            <input className="form-control" value={newProjectCode} onChange={(e) => setNewProjectCode(e.target.value)} />
          </div>
        </div>
      )}

      <div className="row mb-2">
        <div className="col-md-6">
          <label>Remarks</label>
          <input className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </div>
      </div>

      {/* ITEM TABLE — UNCHANGED */}
      <h5>Item Details</h5>
      <div className="row fw-bold mb-2">
        <div className="col-md-1">S. No</div>
        <div className="col-md-5">Item</div>
        <div className="col-md-2">Quantity</div>
        <div className="col-md-2">UOM</div>
        <div className="col-md-2">Remarks</div>
      </div>

      {items.map((row, index) => (
        <React.Fragment key={index}>
          <div className="row mb-1">
            <div className="col-md-1">
              <input className="form-control" value={index + 1} readOnly />
            </div>
            <div className="col-md-5">
              <Select options={itemOptions} value={row.item} onChange={(s) => syncItemSelection(s, index)} />
            </div>
            <div className="col-md-2">
              <input type="number" className="form-control" value={row.qty} onChange={(e) => handleItemFieldChange(index, "qty", e.target.value)} />
            </div>
            <div className="col-md-2">
              <input className="form-control" value={row.uom} readOnly />
            </div>
            <div className="col-md-2">
              <input className="form-control" value={row.remarks} onChange={(e) => handleItemFieldChange(index, "remarks", e.target.value)} />
            </div>
          </div>

          <div className="row mb-3 bg-light py-2 rounded">
            <div className="col-md-1"></div>

            <div className="col-md-2">
              <Select
                options={mmsOptions}
                value={row.item ? mmsOptions.find((o) => o.itemId == row.item.itemId) : null}
                onChange={(s) => syncItemSelection(s, index)}
                placeholder="MMS Code"
                isClearable
              />
            </div>

            <div className="col-md-3">
              <input className="form-control" value={row.mmsName} readOnly />
            </div>

            <div className="col-md-2">
              <input className="form-control" value={row.mmsQuantity} readOnly />
            </div>

            <div className="col-md-2">
              <input className="form-control" value={row.secondaryUom} readOnly />
            </div>

            <div className="col-md-2 d-flex gap-2">
              <button className="btn btn-success btn-sm" onClick={() => addRow(index)}>
                <FontAwesomeIcon icon={faPlus} />
              </button>
              <button className="btn btn-danger btn-sm" onClick={() => deleteRow(index)} disabled={items.length === 1}>
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        </React.Fragment>
      ))}

      <div className="mt-4">
        <button className="btn btn-success" onClick={handleSubmit}>
          Submit
        </button>
      </div>
    </div>
  );
};

export default MRInfo;
